import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Smartphone, Zap, Wifi, Bell, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const QUIET_PATHS = new Set([
  "/",
  "/auth",
  "/privacy",
  "/terms",
  "/blog",
  "/faq",
  "/pricing",
  "/excuses",
  "/install",
]);

const DISMISS_HOURS = 168; // 7 days

const SmartInstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [engaged, setEngaged] = useState(false);

  const quiet = QUIET_PATHS.has(location.pathname) || location.pathname.startsWith("/blog/");

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    const dismissedTime = localStorage.getItem("pwa-prompt-dismissed");
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime, 10)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < DISMISS_HOURS) {
        setDismissed(true);
        return;
      }
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setShowPrompt(false);
    });

    const onScroll = () => {
      if (window.scrollY > 480) setEngaged(true);
    };
    const dwell = window.setTimeout(() => setEngaged(true), 45000);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(dwell);
    };
  }, []);

  useEffect(() => {
    if (quiet || isInstalled || dismissed || !engaged || !deferredPrompt) {
      setShowPrompt(false);
      return;
    }
    setShowPrompt(true);
  }, [quiet, isInstalled, dismissed, engaged, deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
    setDismissed(true);
    setShowPrompt(false);
  };

  if (isInstalled || dismissed || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed bottom-20 left-4 right-4 z-[60] md:left-auto md:right-4 md:max-w-sm"
      >
        <div className="glass-card rounded-2xl p-5 shadow-2xl border border-primary/30 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss install prompt"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg mb-1">Add to your home screen</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Same site, faster open. No App Store required.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <Zap className="w-3 h-3 text-primary" />
                  <span>Instant</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <Wifi className="w-3 h-3 text-accent" />
                  <span>Offline</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <Bell className="w-3 h-3 text-secondary" />
                  <span>Alerts</span>
                </div>
              </div>
              <Button onClick={handleInstall} className="w-full h-10 rounded-xl bg-primary hover:opacity-90 font-display gap-2">
                <Download className="w-4 h-4" />
                Add BailBuddies
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SmartInstallPrompt;
